import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { RefreshCw, Plus, Trash2, Edit3, Save } from "lucide-react";
import PageLayout from "../../../components/layout/PageLayout";
import { useAuth, formatApiError } from "../../../lib/auth";
import api from "../../../lib/api";
import ControlModal, { Field } from "./ControlModal";

const TABS = [
    { k: "config",  l: "Config" },
    { k: "owners",  l: "Fantasy Owners" },
    { k: "hs",      l: "HS Top 50" },
    { k: "players", l: "Road to League" },
    { k: "news",    l: "News Feeds" },
];

const OWNER_FIELDS = [
    ["name","Owner Name"],["team_name","Team Name"],["city","City"],
    ["occupation","Occupation"],["favorite_team","Favorite NFL Team"],
    ["favorite_player","Favorite Player"],["draft_strategy","Draft Strategy"],
    ["weekly_opponent","Weekly Opponent"],["streak","Streak (e.g. W3)"],
    ["championship_odds","Championship Odds"],["photo","Photo URL"],
    ["espn_team_url","ESPN Team URL"],["website","Website"],["interview_url","Interview URL"],
];
const OWNER_NUM_FIELDS = [["record_wins","W"],["record_losses","L"],["points_for","PF"],
    ["points_against","PA"],["league_rank","League Rank"],["power_ranking","DB Power Rank"],
    ["experience_years","Experience yrs"]];

const HS_FIELDS = [["school","School"],["city","City"],["state","State"],
    ["record","Record (e.g. 12-0)"],["coach","Head Coach"],["upcoming_opponent","Upcoming Opponent"],
    ["game_date","Game Date"],["hero_image","Hero Image URL"],["logo","Logo URL"]];
const HS_NUM_FIELDS = [["rank","Rank"],["previous_rank","Previous Rank"]];

const PLAYER_FIELDS = [["name","Name"],["position","Position"],["hometown","Hometown"],
    ["high_school","High School"],["grad_year","Grad Year"],["college","College"],
    ["college_position","College Position"],["commit_status","Commit Status"],
    ["nil_notes","NIL Notes"],["nfl_status","NFL Status (prospect/drafted/active/retired)"],
    ["nfl_team","NFL Team"],["draft_year","Draft Year"],["draft_round","Draft Round"],
    ["draft_pick","Pick #"],["photo","Photo URL"],["hero_image","Hero Image URL"],["highlight_url","Highlight URL"]];

export default function FootballControlRoom() {
    const { user } = useAuth();
    const [tab, setTab] = useState("config");
    const [config, setConfig] = useState(null);
    const [owners, setOwners] = useState([]);
    const [hs, setHs] = useState([]);
    const [players, setPlayers] = useState([]);
    const [sources, setSources] = useState([]);

    // Config editor
    const [cfgForm, setCfgForm] = useState(null);
    const [savingCfg, setSavingCfg] = useState(false);

    // Modals
    const [ownerModal, setOwnerModal] = useState(null);
    const [hsModal, setHsModal] = useState(null);
    const [playerModal, setPlayerModal] = useState(null);
    const [sourceModal, setSourceModal] = useState(null);

    const loadAll = async () => {
        try {
            const [c, o, h, p, s] = await Promise.all([
                api.get("/football/config"),
                api.get("/football/fantasy/owners"),
                api.get("/football/hs/rankings"),
                api.get("/football/players"),
                api.get("/football/news/sources"),
            ]);
            setConfig(c.data); setCfgForm(c.data);
            setOwners(o.data); setHs(h.data); setPlayers(p.data); setSources(s.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { loadAll(); }, []);

    const saveConfig = async () => {
        setSavingCfg(true);
        try {
            const trending = Array.isArray(cfgForm.trending)
                ? cfgForm.trending
                : String(cfgForm.trending || "").split(",").map(x => x.trim()).filter(Boolean);
            const body = { ...cfgForm, trending };
            await api.put("/football/config", body);
            toast.success("Football config saved");
            loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingCfg(false); }
    };

    const refreshNews = async () => {
        try {
            const r = await api.post("/football/news/refresh", {});
            toast.success(`Refreshed feeds: +${r.data.inserted} new articles`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Owner CRUD
    const saveOwner = async () => {
        try {
            const body = { ...ownerModal };
            OWNER_NUM_FIELDS.forEach(([k]) => { if (body[k] !== "" && body[k] != null) body[k] = Number(body[k]) || 0; });
            if (body.id) { await api.patch(`/football/fantasy/owners/${body.id}`, body); toast.success("Owner updated"); }
            else { await api.post("/football/fantasy/owners", body); toast.success("Owner created"); }
            setOwnerModal(null); loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteOwner = async (id) => {
        if (!window.confirm("Delete this owner? This can't be undone.")) return;
        try { await api.delete(`/football/fantasy/owners/${id}`); toast.success("Owner deleted"); loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    // HS CRUD
    const saveHs = async () => {
        try {
            const body = { ...hsModal };
            HS_NUM_FIELDS.forEach(([k]) => { if (body[k] !== "" && body[k] != null) body[k] = Number(body[k]) || 0; });
            if (body.id) { await api.patch(`/football/hs/rankings/${body.id}`, body); toast.success("Team updated"); }
            else { await api.post("/football/hs/rankings", body); toast.success("Team added"); }
            setHsModal(null); loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteHs = async (id) => {
        if (!window.confirm("Delete this ranking? This can't be undone.")) return;
        try { await api.delete(`/football/hs/rankings/${id}`); toast.success("Deleted"); loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    // Player CRUD
    const savePlayer = async () => {
        try {
            const body = { ...playerModal, committed: !!playerModal.committed };
            if (body.id) { await api.patch(`/football/players/${body.id}`, body); toast.success("Player updated"); }
            else { await api.post("/football/players", body); toast.success("Player added"); }
            setPlayerModal(null); loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const deletePlayer = async (id) => {
        if (!window.confirm("Delete this player?")) return;
        try { await api.delete(`/football/players/${id}`); toast.success("Deleted"); loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    // Source CRUD
    const saveSource = async () => {
        try {
            const body = { ...sourceModal };
            if (body.id) { await api.patch(`/football/news/sources/${body.id}`, body); toast.success("Feed updated"); }
            else { await api.post("/football/news/sources", body); toast.success("Feed added"); }
            setSourceModal(null); loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const toggleSource = async (s) => {
        try { await api.patch(`/football/news/sources/${s.id}`, { active: !s.active }); loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteSource = async (id) => {
        if (!window.confirm("Remove this feed?")) return;
        try { await api.delete(`/football/news/sources/${id}`); toast.success("Feed removed"); loadAll(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <PageLayout>
            <section className="pt-28 pb-6 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-3">Digital Beast · Football Control Room</div>
                    <div className="flex flex-wrap justify-between items-end gap-4">
                        <h1 className="font-display text-4xl md:text-5xl text-white tracking-tight" data-testid="control-heading">
                            THE <span className="text-[#d4af37]">CONTROL ROOM</span>
                        </h1>
                        <div className="text-white/50 text-sm">{user?.name} · {user?.role}</div>
                    </div>
                </div>
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-6 flex gap-1 overflow-x-auto">
                    {TABS.map(t => (
                        <button key={t.k} onClick={() => setTab(t.k)} data-testid={`control-tab-${t.k}`}
                            className={`px-4 py-3 text-xs uppercase tracking-widest font-semibold border-b-2 whitespace-nowrap ${tab === t.k ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-white/50 hover:text-white"}`}>{t.l}</button>
                    ))}
                </div>
            </section>

            <section className="py-10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">

                    {/* CONFIG */}
                    {tab === "config" && cfgForm && (
                        <div className="space-y-4 max-w-3xl" data-testid="control-config">
                            <Field label="ESPN League URL" value={cfgForm.espn_league_url} onChange={v => setCfgForm({...cfgForm, espn_league_url: v})} testid="cfg-espn-url" />
                            <div className="grid md:grid-cols-2 gap-4">
                                <Field label="Super Bowl Name" value={cfgForm.super_bowl_name} onChange={v => setCfgForm({...cfgForm, super_bowl_name: v})} testid="cfg-sb-name" />
                                <Field label="Super Bowl Date (ISO 8601)" value={cfgForm.super_bowl_date} onChange={v => setCfgForm({...cfgForm, super_bowl_date: v})} testid="cfg-sb-date" />
                                <Field label="Super Bowl City" value={cfgForm.super_bowl_city} onChange={v => setCfgForm({...cfgForm, super_bowl_city: v})} testid="cfg-sb-city" />
                                <Field label="Super Bowl Stadium" value={cfgForm.super_bowl_stadium} onChange={v => setCfgForm({...cfgForm, super_bowl_stadium: v})} testid="cfg-sb-stadium" />
                            </div>
                            <Field label="Trending topics (comma-separated)" textarea
                                value={Array.isArray(cfgForm.trending) ? cfgForm.trending.join(", ") : cfgForm.trending}
                                onChange={v => setCfgForm({...cfgForm, trending: v})} testid="cfg-trending" />
                            <div className="flex justify-end pt-2">
                                <button onClick={saveConfig} disabled={savingCfg} className="btn-gold" data-testid="cfg-save">
                                    {savingCfg ? "Saving…" : "Save Config"} <Save size={14} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* OWNERS */}
                    {tab === "owners" && (
                        <div data-testid="control-owners">
                            <div className="flex justify-between items-center mb-5">
                                <div className="text-white/60 text-sm">{owners.length} owners · edit rankings, records, photos.</div>
                                <button onClick={() => setOwnerModal({})} className="btn-gold" data-testid="add-owner-btn"><Plus size={14}/> Add Owner</button>
                            </div>
                            <div className="surface overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead><tr className="text-white/40 text-xs uppercase tracking-widest">
                                        <th className="text-left p-4">Rank</th><th className="text-left p-4">Team</th><th className="text-left p-4">Owner</th>
                                        <th className="text-right p-4">W-L</th><th className="text-right p-4">PF</th><th className="text-left p-4">Streak</th><th className="text-right p-4">Power</th><th></th>
                                    </tr></thead>
                                    <tbody>
                                        {owners.map(o => (
                                            <tr key={o.id} className="border-t border-white/10 hover:bg-white/5">
                                                <td className="p-4"><span className="font-display text-2xl text-[#d4af37]">{o.league_rank}</span></td>
                                                <td className="p-4 text-white">{o.team_name}</td>
                                                <td className="p-4 text-white/70">{o.name} · <span className="text-white/40 text-xs">{o.city}</span></td>
                                                <td className="p-4 text-right font-mono text-white">{o.record_wins}-{o.record_losses}</td>
                                                <td className="p-4 text-right font-mono text-white/70">{o.points_for}</td>
                                                <td className="p-4 text-white/60">{o.streak}</td>
                                                <td className="p-4 text-right text-[#d4af37]">#{o.power_ranking}</td>
                                                <td className="p-4">
                                                    <div className="flex gap-2 justify-end">
                                                        <button onClick={() => setOwnerModal({...o})} className="chip" data-testid={`edit-owner-${o.slug}`}><Edit3 size={12}/></button>
                                                        <button onClick={() => deleteOwner(o.id)} className="chip" data-testid={`delete-owner-${o.slug}`}><Trash2 size={12}/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* HS */}
                    {tab === "hs" && (
                        <div data-testid="control-hs">
                            <div className="flex justify-between items-center mb-5">
                                <div className="text-white/60 text-sm">{hs.length} teams · maintain Top 50 rankings weekly.</div>
                                <button onClick={() => setHsModal({rank: hs.length + 1})} className="btn-gold" data-testid="add-hs-btn"><Plus size={14}/> Add Team</button>
                            </div>
                            <div className="surface overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead><tr className="text-white/40 text-xs uppercase tracking-widest">
                                        <th className="text-left p-4">Rank</th><th className="text-left p-4">Prev</th><th className="text-left p-4">School</th>
                                        <th className="text-left p-4">Location</th><th className="text-left p-4">Record</th><th className="text-left p-4">Coach</th><th></th>
                                    </tr></thead>
                                    <tbody>
                                        {hs.map(t => (
                                            <tr key={t.id} className="border-t border-white/10 hover:bg-white/5">
                                                <td className="p-4"><span className="font-display text-2xl text-[#d4af37]">{t.rank}</span></td>
                                                <td className="p-4 text-white/40">{t.previous_rank || "—"}</td>
                                                <td className="p-4 text-white">{t.school}</td>
                                                <td className="p-4 text-white/60">{t.city}, {t.state}</td>
                                                <td className="p-4 text-white/70">{t.record}</td>
                                                <td className="p-4 text-white/60">{t.coach}</td>
                                                <td className="p-4">
                                                    <div className="flex gap-2 justify-end">
                                                        <button onClick={() => setHsModal({...t})} className="chip" data-testid={`edit-hs-${t.slug}`}><Edit3 size={12}/></button>
                                                        <button onClick={() => deleteHs(t.id)} className="chip" data-testid={`delete-hs-${t.slug}`}><Trash2 size={12}/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* PLAYERS */}
                    {tab === "players" && (
                        <div data-testid="control-players">
                            <div className="flex justify-between items-center mb-5">
                                <div className="text-white/60 text-sm">{players.length} players · Road to the League (HS → College → NFL).</div>
                                <button onClick={() => setPlayerModal({nfl_status: "prospect"})} className="btn-gold" data-testid="add-player-btn"><Plus size={14}/> Add Player</button>
                            </div>
                            <div className="surface overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead><tr className="text-white/40 text-xs uppercase tracking-widest">
                                        <th className="text-left p-4">Player</th><th className="text-left p-4">Pos</th><th className="text-left p-4">HS</th>
                                        <th className="text-left p-4">College</th><th className="text-left p-4">NFL</th><th className="text-left p-4">Status</th><th></th>
                                    </tr></thead>
                                    <tbody>
                                        {players.map(p => (
                                            <tr key={p.id} className="border-t border-white/10 hover:bg-white/5">
                                                <td className="p-4 text-white">{p.name}</td>
                                                <td className="p-4 text-white/60">{p.position}</td>
                                                <td className="p-4 text-white/70">{p.high_school || "—"}</td>
                                                <td className="p-4 text-white/70">{p.college || "—"}</td>
                                                <td className="p-4 text-[#d4af37]">{p.nfl_team || "—"}</td>
                                                <td className="p-4"><span className="chip">{p.nfl_status || "prospect"}</span></td>
                                                <td className="p-4">
                                                    <div className="flex gap-2 justify-end">
                                                        <button onClick={() => setPlayerModal({...p})} className="chip" data-testid={`edit-player-${p.slug}`}><Edit3 size={12}/></button>
                                                        <button onClick={() => deletePlayer(p.id)} className="chip" data-testid={`delete-player-${p.slug}`}><Trash2 size={12}/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* NEWS */}
                    {tab === "news" && (
                        <div data-testid="control-news">
                            <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
                                <div className="text-white/60 text-sm">{sources.length} feed{sources.length === 1 ? "" : "s"} · toggle active state or refresh manually.</div>
                                <div className="flex gap-2">
                                    <button onClick={refreshNews} className="btn-outline" data-testid="refresh-news-btn"><RefreshCw size={13}/> Refresh Now</button>
                                    <button onClick={() => setSourceModal({category: "NFL", active: true})} className="btn-gold" data-testid="add-source-btn"><Plus size={14}/> Add Feed</button>
                                </div>
                            </div>
                            <div className="surface overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead><tr className="text-white/40 text-xs uppercase tracking-widest">
                                        <th className="text-left p-4">Name</th><th className="text-left p-4">Category</th>
                                        <th className="text-left p-4">URL</th><th className="text-left p-4">Active</th><th></th>
                                    </tr></thead>
                                    <tbody>
                                        {sources.map(s => (
                                            <tr key={s.id} className="border-t border-white/10 hover:bg-white/5">
                                                <td className="p-4 text-white">{s.name}</td>
                                                <td className="p-4"><span className="chip">{s.category}</span></td>
                                                <td className="p-4 text-white/60 text-xs truncate max-w-md"><a href={s.url} target="_blank" rel="noreferrer" className="hover:text-[#d4af37]">{s.url}</a></td>
                                                <td className="p-4">
                                                    <button onClick={() => toggleSource(s)} className={`chip ${s.active ? "chip-active" : ""}`} data-testid={`toggle-source-${s.id}`}>{s.active ? "ON" : "off"}</button>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex gap-2 justify-end">
                                                        <button onClick={() => setSourceModal({...s})} className="chip" data-testid={`edit-source-${s.id}`}><Edit3 size={12}/></button>
                                                        <button onClick={() => deleteSource(s.id)} className="chip" data-testid={`delete-source-${s.id}`}><Trash2 size={12}/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* MODALS */}
            <ControlModal open={!!ownerModal} title={ownerModal?.id ? "Edit Owner" : "Add Owner"}
                onClose={() => setOwnerModal(null)} onSubmit={saveOwner} testid="owner-modal">
                {ownerModal && (
                    <div className="space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                            {OWNER_FIELDS.map(([k, l]) => (
                                <Field key={k} label={l} value={ownerModal[k]} onChange={v => setOwnerModal({...ownerModal, [k]: v})} testid={`owner-field-${k}`} />
                            ))}
                        </div>
                        <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                            {OWNER_NUM_FIELDS.map(([k, l]) => (
                                <Field key={k} label={l} type="number" value={ownerModal[k]} onChange={v => setOwnerModal({...ownerModal, [k]: v})} testid={`owner-num-${k}`} />
                            ))}
                        </div>
                        <Field label="Bio" textarea value={ownerModal.bio} onChange={v => setOwnerModal({...ownerModal, bio: v})} testid="owner-field-bio" />
                        <Field label="Trash Talk" textarea value={ownerModal.trash_talk} onChange={v => setOwnerModal({...ownerModal, trash_talk: v})} testid="owner-field-trash-talk" />
                    </div>
                )}
            </ControlModal>

            <ControlModal open={!!hsModal} title={hsModal?.id ? "Edit HS Team" : "Add HS Team"}
                onClose={() => setHsModal(null)} onSubmit={saveHs} testid="hs-modal">
                {hsModal && (
                    <div className="space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                            {HS_NUM_FIELDS.map(([k, l]) => (
                                <Field key={k} label={l} type="number" value={hsModal[k]} onChange={v => setHsModal({...hsModal, [k]: v})} testid={`hs-num-${k}`} />
                            ))}
                            {HS_FIELDS.map(([k, l]) => (
                                <Field key={k} label={l} value={hsModal[k]} onChange={v => setHsModal({...hsModal, [k]: v})} testid={`hs-field-${k}`} />
                            ))}
                        </div>
                        <Field label="Notes" textarea value={hsModal.notes} onChange={v => setHsModal({...hsModal, notes: v})} testid="hs-field-notes" />
                    </div>
                )}
            </ControlModal>

            <ControlModal open={!!playerModal} title={playerModal?.id ? "Edit Player" : "Add Player"}
                onClose={() => setPlayerModal(null)} onSubmit={savePlayer} testid="player-modal">
                {playerModal && (
                    <div className="space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                            {PLAYER_FIELDS.map(([k, l]) => (
                                <Field key={k} label={l} value={playerModal[k]} onChange={v => setPlayerModal({...playerModal, [k]: v})} testid={`player-field-${k}`} />
                            ))}
                        </div>
                        <Field label="Bio" textarea value={playerModal.bio} onChange={v => setPlayerModal({...playerModal, bio: v})} testid="player-field-bio" />
                    </div>
                )}
            </ControlModal>

            <ControlModal open={!!sourceModal} title={sourceModal?.id ? "Edit Feed" : "Add Feed"}
                onClose={() => setSourceModal(null)} onSubmit={saveSource} testid="source-modal">
                {sourceModal && (
                    <div className="space-y-3">
                        <Field label="Feed Name" value={sourceModal.name} onChange={v => setSourceModal({...sourceModal, name: v})} testid="source-field-name" />
                        <Field label="Feed URL (RSS/Atom)" value={sourceModal.url} onChange={v => setSourceModal({...sourceModal, url: v})} testid="source-field-url" />
                        <div>
                            <label className="db-label">Category</label>
                            <select className="db-input-block" value={sourceModal.category || "NFL"} onChange={e => setSourceModal({...sourceModal, category: e.target.value})} data-testid="source-field-category">
                                <option value="NFL">NFL</option>
                                <option value="College">College</option>
                                <option value="High School">High School</option>
                                <option value="Fantasy">Fantasy</option>
                            </select>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={!!sourceModal.active} onChange={e => setSourceModal({...sourceModal, active: e.target.checked})} className="accent-[#d4af37]" data-testid="source-field-active" />
                            <span className="text-white/80 text-sm">Active</span>
                        </label>
                    </div>
                )}
            </ControlModal>
        </PageLayout>
    );
}
